import { useEffect, useRef, useState } from "react";
import { Plus, X } from "react-feather";
import styles from "./photopicker.module.css";

export const MAX_VEHICLE_PHOTOS = 6;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const VALID_EXTENSIONS = ["jpg", "jpeg", "png"];

let nextKey = 0;
const newKey = () => `p${++nextKey}`;

// An existing vehicle photo ({ id, url, thumb }) as a picker item; id null = legacy single image
export const existingPhotoItem = (photo) => ({ key: newKey(), id: photo?.id ?? null, url: photo?.thumb || photo?.url || "", file: null });

/*
 * Vehicle photos for the admin Add / Edit forms: unlabelled thumbnails with remove buttons and an "Add photos" tile.
 * Controlled: photos is [{ key, id, url, file }] (file set for new uploads, id for kept existing photos).
 * Preview urls of new files are revoked when removed and when the picker unmounts.
 */
const VehiclePhotoPicker = ({ photos, onChange, max = MAX_VEHICLE_PHOTOS, disabled = false, inputId = "vehicle-photos" }) => {
  const [error, setError] = useState("");
  const list = photos || [];

  const photosRef = useRef(list);
  photosRef.current = list;
  useEffect(() => () => photosRef.current.forEach((photo) => { if (photo.file) URL.revokeObjectURL(photo.url); }), []);

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setError("");

    const accepted = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (VALID_EXTENSIONS.indexOf(ext) === -1) {
        setError("Photos must be JPG or PNG images.");
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        setError("Each photo must be 5 MB or smaller.");
        continue;
      }
      accepted.push({ key: newKey(), id: null, url: URL.createObjectURL(file), file });
    }

    let next = list.concat(accepted);
    if (next.length > max) {
      setError(`You can upload up to ${max} photos.`);
      next.slice(max).forEach((photo) => { if (photo.file) URL.revokeObjectURL(photo.url); });
      next = next.slice(0, max);
    }
    onChange(next);
  };

  const removePhoto = (key) => {
    const photo = list.find((item) => item.key === key);
    if (photo?.file) URL.revokeObjectURL(photo.url);
    onChange(list.filter((item) => item.key !== key));
  };

  return (<div className={styles.Picker}>
    <div className={styles.Head}>
      <span className={styles.Title}>Photos</span>
      <span className={styles.Count}>{list.length} of {max}</span>
    </div>
    <div className={styles.Grid}>
      {list.map((photo, index) => (<div key={photo.key} className={styles.Thumb}>
        <img src={photo.url} alt={`Vehicle photo ${index + 1}`} />
        <button type="button" aria-label="Remove photo" className={styles.Remove} disabled={disabled} onClick={() => removePhoto(photo.key)}><X size={12} /></button>
      </div>))}
      {list.length < max && (<label htmlFor={inputId} className={disabled ? `${styles.Add} ${styles.AddDisabled}` : styles.Add}>
        <Plus size={18} />
        <span>Add photos</span>
        <input id={inputId} type="file" multiple accept="image/jpeg,image/png" onChange={addPhotos} disabled={disabled} />
      </label>)}
    </div>
    <p className={styles.Hint}>JPG or PNG · up to {max} photos · 5 MB each</p>
    {error !== "" && <p className={styles.Error}>{error}</p>}
  </div>);
};

export default VehiclePhotoPicker;
